import {
  formatHprd,
  hprdStatus,
  isHprdUnhealthy,
} from '@/entities/staff/lib/hprd';
import { isStaffingRatioUnhealthy } from '@/entities/staff/lib/staffing-ratio-utils';
import { useStaffingMetric } from '@/entities/staff/lib/use-staffing-metric';
import type { GetStaffingRatioResponseDto } from '@/shared/api/modules/staff';
import { FocusSummaryCard } from '@/shared/components';
import { cn } from '@/shared/lib/cn';
import { useFacilityScope } from '@/shared/lib/hooks/use-facility-scope';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import type { FC } from 'react';
import { getStaffingRatioQueryOptions } from '../api/get-staffing-ratio.query';

const OVERSTAFFED_THRESHOLD = 1.3;
const UNDERSTAFFED_THRESHOLD = 2.5;

function getStaffingStatusLabel(
  ratioValue: number | null
): { label: string; className: string } | null {
  if (ratioValue === null) return null;
  if (ratioValue > UNDERSTAFFED_THRESHOLD) {
    return {
      label: 'Understaffed',
      className: 'border-dust-red-5 bg-dust-red-1 text-dust-red-5',
    };
  }
  if (ratioValue < OVERSTAFFED_THRESHOLD) {
    return {
      label: 'Overstaffed',
      className: 'border-dust-red-5 bg-dust-red-1 text-dust-red-5',
    };
  }
  return {
    label: 'Optimally Staffed',
    className: 'border-polar-green-6 bg-polar-green-1 text-polar-green-7',
  };
}

interface StaffingRatioCardProps {
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
  facilityId?: number;
  showStatusLabel?: boolean;
}

const sparklineToChartData = (
  sparkline: GetStaffingRatioResponseDto['sparkline']
) => {
  const values = sparkline.points.map((p) => p.value);
  const labels = sparkline.points.map((p) => p.end);

  const cleanedValues: number[] = [];
  const cleanedLabels: string[] = [];

  values.forEach((val, i) => {
    if (i === 0 || val !== values[i - 1]) {
      cleanedValues.push(val ?? 0);
      cleanedLabels.push(labels[i]);
    }
  });

  return {
    labels: cleanedLabels,
    datasets: [
      {
        label: sparkline.granularity,
        data: cleanedValues,
        borderColor: '#165BAA',
        backgroundColor: 'rgba(22, 91, 170, 0.2)',
      },
    ],
  };
};

export const StaffingRatioCard: FC<StaffingRatioCardProps> = ({
  dateRange,
  facilityId,
  showStatusLabel = false,
}) => {
  const { scopedFacilityId } = useFacilityScope({ facilityId });
  const { isHprd, title, tooltip } = useStaffingMetric();
  const { data, isLoading } = useQuery(
    getStaffingRatioQueryOptions({
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      facilitiesIds: facilityId
        ? [facilityId]
        : scopedFacilityId
          ? [scopedFacilityId]
          : undefined,
    })
  );

  // The value the card is about: HPRD for staff from Sling, the old ratio otherwise.
  const metricValue = isHprd
    ? (data?.hprdValue ?? null)
    : (data?.ratioValue ?? null);

  const isUnhealthy =
    metricValue !== null && data?.changePct != null
      ? isHprd
        ? isHprdUnhealthy(metricValue, data.changePct)
        : isStaffingRatioUnhealthy(metricValue, data.changePct)
      : false;

  const indicator =
    data?.changePct !== null && data?.changePct !== undefined && metricValue
      ? {
          value: Math.abs(data.changePct),
          negativeValue: data.changePct < 0,
          negativeAsPositive: false,
          className: isUnhealthy ? 'text-dust-red-5' : 'text-polar-green-7',
        }
      : undefined;

  const sparklineData = data?.sparkline
    ? { data: sparklineToChartData(data.sparkline) }
    : undefined;

  const statusLabel =
    showStatusLabel && !isLoading
      ? isHprd
        ? hprdStatus(metricValue)
        : getStaffingStatusLabel(metricValue)
      : null;

  return (
    <FocusSummaryCard
      icon="team"
      title={title}
      indicator={indicator}
      tooltipMessage={tooltip}
      chart={showStatusLabel ? undefined : sparklineData}
      valueAsComment={isHprd && metricValue === null && !!data?.message}
      value={
        isHprd
          ? metricValue === null && data?.message
            ? data.message
            : formatHprd(metricValue)
          : (data?.ratio ?? 'N/A')
      }
      topRightAddon={
        statusLabel ? (
          <span
            className={cn(
              'inline-block rounded border px-2 py-0.5 text-xs font-medium',
              statusLabel.className
            )}
          >
            {statusLabel.label}
          </span>
        ) : undefined
      }
      loading={isLoading}
    />
  );
};
